/**
 * AWS SDK Configuration Loader
 *
 * This module exports AWS configuration constants from environment variables
 * for direct AWS SDK usage with Apollo Client and AWS services.
 *
 * All configuration values are validated at runtime to ensure proper setup.
 */
// Client-side accessible env map (Next.js inlines NEXT_PUBLIC_* at build time)
const CLIENT_ENV = {
  NEXT_PUBLIC_AWS_REGION: process.env.NEXT_PUBLIC_AWS_REGION,
  NEXT_PUBLIC_APPSYNC_ENDPOINT: process.env.NEXT_PUBLIC_APPSYNC_ENDPOINT,
  NEXT_PUBLIC_APPSYNC_REGION: process.env.NEXT_PUBLIC_APPSYNC_REGION,
  NEXT_PUBLIC_APPSYNC_AUTHENTICATION_TYPE: process.env.NEXT_PUBLIC_APPSYNC_AUTHENTICATION_TYPE,
  NEXT_PUBLIC_APPSYNC_API_KEY: process.env.NEXT_PUBLIC_APPSYNC_API_KEY,
  NEXT_PUBLIC_COGNITO_USER_POOL_ID: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID,
  NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID,
  NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID: process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID,
  NEXT_PUBLIC_COGNITO_REGION: process.env.NEXT_PUBLIC_COGNITO_REGION,
  NEXT_PUBLIC_S3_BUCKET_NAME: process.env.NEXT_PUBLIC_S3_BUCKET_NAME,
  NEXT_PUBLIC_S3_REGION: process.env.NEXT_PUBLIC_S3_REGION,
  NEXT_PUBLIC_ADMIN_QUERIES_LAMBDA_ARN: process.env.NEXT_PUBLIC_ADMIN_QUERIES_LAMBDA_ARN,
  NEXT_PUBLIC_OUTLOOK_WEBHOOK_LAMBDA_ARN: process.env.NEXT_PUBLIC_OUTLOOK_WEBHOOK_LAMBDA_ARN,
  NEXT_PUBLIC_ADMIN_QUERIES_ENDPOINT: process.env.NEXT_PUBLIC_ADMIN_QUERIES_ENDPOINT,
  NEXT_PUBLIC_OUTLOOK_WEBHOOK_ENDPOINT: process.env.NEXT_PUBLIC_OUTLOOK_WEBHOOK_ENDPOINT,
  NEXT_PUBLIC_BACKEND_NAME: process.env.NEXT_PUBLIC_BACKEND_NAME,
  NEXT_PUBLIC_ENABLE_MIGRATION: process.env.NEXT_PUBLIC_ENABLE_MIGRATION,
  NEXT_PUBLIC_USE_AWS_SDK_CONFIG: process.env.NEXT_PUBLIC_USE_AWS_SDK_CONFIG,
  NEXT_PUBLIC_USE_AWS_SDK_AUTH: process.env.NEXT_PUBLIC_USE_AWS_SDK_AUTH,
  NEXT_PUBLIC_USE_AWS_SDK_GRAPHQL: process.env.NEXT_PUBLIC_USE_AWS_SDK_GRAPHQL,
  NEXT_PUBLIC_USE_AWS_SDK_STORAGE: process.env.NEXT_PUBLIC_USE_AWS_SDK_STORAGE,
  NEXT_PUBLIC_USE_AWS_SDK_REPOSITORIES: process.env.NEXT_PUBLIC_USE_AWS_SDK_REPOSITORIES,
  NEXT_PUBLIC_USE_AWS_SDK_API_ROUTES: process.env.NEXT_PUBLIC_USE_AWS_SDK_API_ROUTES,
  NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
} as const;

type PublicEnvKey = keyof typeof CLIENT_ENV;

// IMPORTANT: Use statically inlined NEXT_PUBLIC_* values for BOTH server and client.
// On Amplify SSR Lambdas, .env.local is not available at runtime, so dynamic
// process.env lookups will be undefined. Next.js inlines direct references at build time.
function getRequiredEnv(key: PublicEnvKey, description: string): string {
  const value = CLIENT_ENV[key];
  const enableMigration = CLIENT_ENV.NEXT_PUBLIC_ENABLE_MIGRATION;

  if (!value || String(value).trim() === '') {
    if (enableMigration !== 'true') {
      return '';
    }

    throw new Error(
      `Missing required environment variable: ${key}\n` +
        `Description: ${description}\n` +
        `Please ensure this variable is set via Terraform-generated .env.local before build.`
    );
  }

  return String(value);
}

function getOptionalEnv(key: PublicEnvKey, defaultValue: string = ''): string {
  const value = CLIENT_ENV[key];
  return (value as string) || defaultValue;
}

/**
 * AWS Region Configuration
 */
export const AWS_REGION = getRequiredEnv(
  'NEXT_PUBLIC_AWS_REGION',
  'AWS region where your resources are deployed (e.g., ap-northeast-1)'
);

/**
 * AWS AppSync GraphQL Configuration
 */
export const APPSYNC_ENDPOINT = getRequiredEnv(
  'NEXT_PUBLIC_APPSYNC_ENDPOINT',
  'GraphQL API endpoint for AWS AppSync'
);

export const APPSYNC_REGION = getOptionalEnv('NEXT_PUBLIC_APPSYNC_REGION', AWS_REGION);

export const APPSYNC_AUTHENTICATION_TYPE = getOptionalEnv(
  'NEXT_PUBLIC_APPSYNC_AUTHENTICATION_TYPE',
  'AMAZON_COGNITO_USER_POOLS'
);

export const APPSYNC_API_KEY = getOptionalEnv('NEXT_PUBLIC_APPSYNC_API_KEY', '');

/**
 * AWS Cognito Configuration
 */
export const COGNITO_USER_POOL_ID = getRequiredEnv(
  'NEXT_PUBLIC_COGNITO_USER_POOL_ID',
  'Cognito User Pool ID for authentication'
);

export const COGNITO_USER_POOL_CLIENT_ID = getRequiredEnv(
  'NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID',
  'Cognito User Pool Web Client ID'
);

export const COGNITO_IDENTITY_POOL_ID = getRequiredEnv(
  'NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID',
  'Cognito Identity Pool ID for temporary AWS credentials'
);

export const COGNITO_REGION = getOptionalEnv('NEXT_PUBLIC_COGNITO_REGION', AWS_REGION);

/**
 * AWS S3 Configuration
 */
export const S3_BUCKET_NAME = getRequiredEnv(
  'NEXT_PUBLIC_S3_BUCKET_NAME',
  'S3 bucket name for file storage'
);

export const S3_REGION = getOptionalEnv('NEXT_PUBLIC_S3_REGION', AWS_REGION);

/**
 * AWS Lambda Configuration
 */
export const ADMIN_QUERIES_LAMBDA_ARN = getOptionalEnv('NEXT_PUBLIC_ADMIN_QUERIES_LAMBDA_ARN', '');

export const OUTLOOK_WEBHOOK_LAMBDA_ARN = getOptionalEnv(
  'NEXT_PUBLIC_OUTLOOK_WEBHOOK_LAMBDA_ARN',
  ''
);

/**
 * API Gateway Configuration
 */
export const ADMIN_QUERIES_ENDPOINT = getOptionalEnv('NEXT_PUBLIC_ADMIN_QUERIES_ENDPOINT', '');

export const OUTLOOK_WEBHOOK_ENDPOINT = getOptionalEnv('NEXT_PUBLIC_OUTLOOK_WEBHOOK_ENDPOINT', '');

/**
 * Application Configuration
 */
export const BACKEND_NAME = getOptionalEnv('NEXT_PUBLIC_BACKEND_NAME', 'development');

/**
 * AWS Services Configuration Mapping
 *
 * This object provides a structured mapping of AWS service configurations
 * from environment variables to a format compatible with AWS SDK and Apollo Client.
 */
export const awsConfigMapping = {
  aws_project_region: AWS_REGION,
  aws_appsync_graphqlEndpoint: APPSYNC_ENDPOINT,
  aws_appsync_region: APPSYNC_REGION,
  aws_appsync_authenticationType: APPSYNC_AUTHENTICATION_TYPE,
  aws_appsync_apiKey: APPSYNC_API_KEY,
  aws_cognito_identity_pool_id: COGNITO_IDENTITY_POOL_ID,
  aws_cognito_region: COGNITO_REGION,
  aws_user_pools_id: COGNITO_USER_POOL_ID,
  aws_user_pools_web_client_id: COGNITO_USER_POOL_CLIENT_ID,
  aws_user_files_s3_bucket: S3_BUCKET_NAME,
  aws_user_files_s3_bucket_region: S3_REGION,
  aws_cloud_logic_custom: [
    ...(ADMIN_QUERIES_ENDPOINT
      ? [
          {
            name: 'AdminQueries',
            endpoint: ADMIN_QUERIES_ENDPOINT,
            region: AWS_REGION,
          },
        ]
      : []),
    ...(OUTLOOK_WEBHOOK_ENDPOINT
      ? [
          {
            name: 'OutlookCalendarWebhook',
            endpoint: OUTLOOK_WEBHOOK_ENDPOINT,
            region: AWS_REGION,
          },
        ]
      : []),
  ],
};

/**
 * Validation function to ensure all required configurations are present
 * Call this at application startup to fail fast if configuration is invalid
 */
export function validateAwsConfig(): void {
  try {
    const _check =
      AWS_REGION &&
      APPSYNC_ENDPOINT &&
      COGNITO_USER_POOL_ID &&
      COGNITO_USER_POOL_CLIENT_ID &&
      COGNITO_IDENTITY_POOL_ID &&
      S3_BUCKET_NAME;

    if (!_check) {
      throw new Error('Missing required AWS configuration');
    }

    console.log('✓ AWS SDK configuration validated successfully');
    console.log(`  Region: ${AWS_REGION}`);
    console.log(`  Backend: ${BACKEND_NAME}`);
  } catch (error) {
    console.error('✗ AWS SDK configuration validation failed');
    throw error;
  }
}

if (typeof window !== 'undefined') {
  try {
    validateAwsConfig();
  } catch (error) {
    console.error('Failed to validate AWS configuration:', error);
  }
}
